// Dependencies
import React, { useRef, useEffect, useState } from "react"
import { graphql, useStaticQuery, Link, navigate } from "gatsby"
// import { useSpring, animated } from 'react-spring'
// import { useLocation } from "@reach/router"
// Hooks & Helpers
// import priceLabel from '../helpers/priceLabel'
// import useOnClickOutside from '../hooks/useOnClickOutside'
// import useLineItem from '../hooks/useLineItem'
// import useVariantInfo from '../hooks/useVariantInfo'
// Components
// import Link from '../components/Link'
// import Markdown from '../components/Markdown'
import MenuProductInfo from "../components/MenuProductInfo"
import { MenuCartLink } from "../components/MenuCart"
// Wrappers
import { useNavigationContext } from "../wrappers/Navigation"
// import { useStoreContext } from '../wrappers/Store'

export function useMenuPositioning(focused) {
  const [{ x, y }, setPosition] = useState({ x: 0, y: 0 })
  // Bail out when the position is unchanged so an identical reposition() call
  // returns the same state object — React then skips the re-render, which
  // prevents a re-render → re-measure → reposition loop (menu content is
  // rebuilt every render) that would keep restarting the transform transition.
  const reposition = (coords) =>
    setPosition((prev) =>
      prev.x === coords.x && prev.y === coords.y ? prev : coords
    )

  const menuTransform = `translateX(${x + 12}px) translateY(0%)`

  return { reposition, menuTransform, x }
}

export const MenuHotspot = () => {
  const { menuState } = useNavigationContext()
  const { handleMenuFocus, isMenuFocused } = menuState

  const focus = () => {
    handleMenuFocus(true)
  }

  return !isMenuFocused && <div className="menu__hotspot" onClick={focus} />
}

export const MenuHeader = ({ style }) => {
  const { menuState } = useNavigationContext()
  const { toggleMenuFocus, isMenuFocused, handleMenuFocus, setActivePath } =
    menuState

  const handleClick = () => {
    if (isMenuFocused) {
      handleMenuFocus(false)
      setActivePath(null)
    } else {
      handleMenuFocus(true)
    }
  }

  return (
    <header
      className={`menu__header ${isMenuFocused ? "open" : ""}`}
      style={style}
    >
      <button className="btn" onClick={handleClick}>
        {isMenuFocused ? "Close" : "Menu"}
      </button>
    </header>
  )
}

const MenuBranch = ({
  branch,
  sharedProps,
  depth = 1,
  parentMatch = null,
  columnIsActive = true,
}) => {
  // Props
  const {
    menuState,
    currentMenuPath,
    currentProduct,
    setMenuPath,
    menuRef,
    reposition,
    isShifted,
  } = sharedProps
  const { activePath, setActivePath, handleMenuFocus, isMenuFocused } =
    menuState

  // Refs
  const positionRef = useRef()
  const branchesRef = useRef()
  // When a touch returns to this menu, the collapse makes this column active and
  // its link clickable again — so the ghost click that follows the tap would
  // otherwise open the item. This flags that click to be ignored once.
  const ignoreNextClickRef = useRef(false)

  // Content
  const { label, match, path, href, links, willShowProduct, willShowCart } =
    branch

  // UX
  const hasActivePath = activePath?.includes(match)
  const hasCurrentPage = currentMenuPath?.includes(match)
  const isCurrentPage = currentMenuPath === match
  const willShowBranch = activePath ? hasActivePath : hasCurrentPage
  // The deepest open item (the last one clicked, or the current page on load).
  const isActiveLeaf = !!activePath && activePath === match
  // This branch is the deepest open one, so its own submenu is the active menu.
  const isDeepestOpen = activePath ? isActiveLeaf : isCurrentPage

  // Only show product info if:
  // 1. We have a currentProduct AND
  // 2. willShowProduct is true AND
  // 3. Either this is the current page OR this is a dynamically added menu item for a product type
  const isDynamicProductType =
    path &&
    currentMenuPath &&
    currentMenuPath.startsWith("/shop/") &&
    path.includes(currentMenuPath) &&
    path.split("/").length >= 4

  const willShowProductBranch =
    currentProduct && willShowProduct && (isCurrentPage || isDynamicProductType)

  // console.log(currentMenuPath);

  // When this branch is the deepest open column, check whether it fits in the
  // remaining screen width. If not, slide the WHOLE menu left so its right edge
  // sits flush against the right edge of the screen. Only the deepest open
  // branch drives this, and it recomputes on open and on resize.
  useEffect(() => {
    if (!isDeepestOpen) return

    const MARGIN = 12

    const measure = () => {
      if (!menuRef.current) return
      // Measure the deepest visible element: the open submenu if there is one,
      // otherwise this branch's own column.
      const el =
        links && willShowBranch && branchesRef.current
          ? branchesRef.current
          : positionRef.current
      if (!el) return

      // anchorOffsetRight is the right edge relative to the menu's own origin,
      // so it's independent of the menu's current transform — no drift across
      // repeated measures.
      const menuLeft = menuRef.current.getBoundingClientRect().left
      const anchorRight = el.getBoundingClientRect().right
      const anchorOffsetRight = anchorRight - menuLeft

      // Clamp to <= 0: only ever slide left, never leave a gap on the left.
      // Round to whole pixels so sub-pixel rect jitter can't nudge the target.
      const x = Math.round(
        Math.min(0, window.innerWidth - MARGIN - anchorOffsetRight - 12)
      )
      reposition({ x, y: 0 })
    }

    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
    // Depend on `!!links` (a stable primitive), NOT the `links` array itself:
    // menu content is rebuilt on every render, so the array reference changes
    // each time and would otherwise re-run this effect on every render.
  }, [isCurrentPage, isActiveLeaf, willShowBranch, activePath, !!links, isMenuFocused])

  // UI
  // Stop propagation so a normal item click doesn't also bubble to the branch's
  // handleParentClick (which would otherwise treat it as a return-to-menu).
  const handleLinkClick = (event) => {
    event.stopPropagation()

    // Swallow the ghost click that follows a touch return-to-menu, so the item
    // the user tapped to go back isn't also opened/navigated. preventDefault
    // also stops Gatsby <Link> navigation.
    if (ignoreNextClickRef.current) {
      ignoreNextClickRef.current = false
      event.preventDefault()
      return
    }

    if (path) {
      if (isCurrentPage) {
        handleMenuFocus(false)
      }

      if (path === "/") {
        setMenuPath("/")
        setActivePath(null)
      } else {
        setActivePath(match)
      }
    } else {
      setActivePath(match)
    }
  }

  // While the menu is slid left to fit, a parent menu's links are
  // pointer-events:none (see menu.css), so a tap/click lands here on the branch
  // instead. It returns to this menu — closing the deeper levels — without
  // triggering the clicked item. Wired to touchstart as well as click so the
  // return (and the resulting slide-right that reveals the parents) feels
  // instant on touch rather than waiting for the tap to complete.
  const handleParentClick = (event) => {
    // Only return-to-menu when the menu is slid left to fit. If everything fits
    // on screen, parent menus stay normally interactive.
    if (!isShifted) return
    // Consume the event at the innermost touched branch so it can't bubble up to
    // shallower parent columns and over-collapse the chain. Touch events bubble,
    // and the active column's link has no touch handler of its own.
    event.stopPropagation()
    // The active (deepest) column keeps normal item taps — don't collapse it.
    if (columnIsActive) return
    event.preventDefault()
    // On touch the collapse re-enables this column's link before the ghost click
    // fires, so flag that click to be ignored. (Cleared as a safety in case no
    // click follows, e.g. the touch became a scroll.)
    if (event.type === "touchstart") {
      ignoreNextClickRef.current = true
      setTimeout(() => {
        ignoreNextClickRef.current = false
      }, 500)
    }
    if (parentMatch) {
      // Open this menu in its default state — its own submenu shown, nothing
      // deeper expanded.
      setActivePath(parentMatch)
    } else {
      // Returning to the root menu: reset to its default state. Clearing
      // activePath alone would fall back to the current page's path and re-open
      // that whole chain, so also reset the menu path (matches the home click).
      setMenuPath("/")
      setActivePath(null)
    }
  }

  return (
    <div
      className={`menu__branch prel ${links ? "prel" : ""} ${isCurrentPage ? "test" : ""
        } ${!columnIsActive && isShifted ? "is-parent-menu" : ""}`}
      ref={positionRef}
      onClick={handleParentClick}
      onTouchStart={handleParentClick}
    >
      <MenuHotspot />

      {href ? (
        <a
          href={href}
          className={`menu__link`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="label">{label}</div>
        </a>
      ) : willShowCart ? (
        <MenuCartLink handleMenuFocus={handleMenuFocus} />
      ) : path && (!links || depth > 1) ? (
        <Link
          to={path}
          className={`menu__link ${willShowBranch ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="label">{label}</div>
          {(links ||
            (currentProduct && (isCurrentPage || isDynamicProductType))) && (
              <div className={`line ${willShowProductBranch ? "is-last" : ""}`} />
            )}
        </Link>
      ) : (
        <div
          className={`menu__link ${willShowBranch ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="label">{label}</div>
          {(links ||
            (currentProduct && (isCurrentPage || isDynamicProductType))) && (
              <div className={`line ${willShowProductBranch ? "is-last" : ""}`} />
            )}
        </div>
      )}

      {links ? (
        <div
          ref={branchesRef}
          className={`menu__branches branch-wrapper ${willShowBranch ? "active" : ""
            }`}
        >
          {links.map((linkBranch, index) => (
            <MenuBranch
              branch={linkBranch}
              sharedProps={sharedProps}
              depth={depth + 1}
              parentMatch={match}
              columnIsActive={isDeepestOpen}
              key={`${linkBranch.match}${index}`}
            />
          ))}
        </div>
      ) : willShowProductBranch ? (
        <MenuProductInfo product={currentProduct} positionRef={positionRef} />
      ) : null}
    </div>
  )
}

const Menu = () => {
  // Context
  const navigationContext = useNavigationContext()
  const { menuState, currentMenuPath, setMenuPath } = navigationContext

  // Refs
  const menuRef = useRef()
  const focusTimer = useRef()

  // Content
  let content = useMenuContent()

  // Check if we're on a product dept route that doesn't match any existing menu items
  if (currentMenuPath && currentMenuPath.startsWith("/shop/")) {
    // Extract department from path (e.g., /shop/mens -> mens)
    const pathParts = currentMenuPath.split("/")
    if (pathParts.length >= 3) {
      const category = pathParts[1] // 'shop'
      const department = pathParts[2] // e.g., 'mens'

      // Check if this department exists in the menu
      const departmentExists = content.some((item) => {
        if (item.match && item.match.includes(`/${category}/${department}`)) {
          return true
        }
        return false
      })

      // If department doesn't exist in menu, add it
      if (!departmentExists) {
        const { currentProduct } = navigationContext

        const newDepartmentItem = {
          label: department.charAt(0).toUpperCase() + department.slice(1), // Capitalize first letter
          path: `/${category}/${department}`,
          match: `/${category}/${department}`,
          links: [
            {
              label: "All",
              path: `/${category}/${department}#thumbnails`,
              match: `/${category}/${department}/all`,
            },
          ],
        }

        // If there's a product type in the URL, add it to the links
        if (pathParts.length >= 4 && pathParts[3]) {
          const productType = pathParts[3]
          const productTypePath = `/${category}/${department}/${productType}`

          // Set the match to exactly match the current path
          const productTypeMatch = currentMenuPath || productTypePath

          newDepartmentItem.links.push({
            label: productType.charAt(0).toUpperCase() + productType.slice(1), // Capitalize first letter
            path: productTypePath,
            match: productTypeMatch,
            willShowProduct: true, // Always set to true for dynamic menu items
          })
        }

        // Add the new department to the beginning of the content array
        content = [newDepartmentItem, ...content]
      }
    }
  }

  // UX
  const { isMenuFocused, handleMenuFocus, setActivePath, activePath } = menuState
  const { reposition, menuTransform, x } = useMenuPositioning(isMenuFocused)
  // The menu has been slid left because the open columns are too wide to fit.
  // The parent-menu return behaviour only applies in this state.
  const isShifted = x < 0

  // UI
  const handleMouseMove = (event) => {
    if (window.matchMedia("(hover: hover)").matches) {
      clearTimeout(focusTimer.current)
      if (event.clientX < 24) {
        handleMenuFocus(true)
      }
    }
  }

  const handleMouseLeave = () => {
    if (window.matchMedia("(hover: hover)").matches) {
      handleMenuFocus(false)
      setActivePath(null)
    }
  }

  // The menu columns don't fill the screen height, so there's empty background
  // around them. While the menu is slid left to fit, touching that background
  // steps back one level — same idea as tapping a parent column, but the
  // background is a much bigger target. Touches on columns never reach here:
  // their branch handlers stopPropagation while shifted.
  const handleBackgroundTouch = () => {
    if (!isShifted || !activePath) return
    const parent = activePath.replace(/\/[^/]+$/, "")
    if (parent) {
      setActivePath(parent)
    } else {
      // Was a top-level menu (e.g. "/chapters") — reset to the default root.
      setMenuPath("/")
      setActivePath(null)
    }
  }

  // Pass props
  const sharedProps = {
    ...navigationContext,
    menuRef,
    reposition,
    isShifted,
  }

  // Style
  const style = {
    transform: menuTransform,
    transition: "0.75s",
  }

  return (
    <nav
      className={`menu ${isMenuFocused ? "focused" : ""}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleBackgroundTouch}
      ref={menuRef}
      style={style}
    >
      <div className="menu__branches branch-wrapper active">
        {content.map((branch, index) => (
          <MenuBranch
            branch={branch}
            sharedProps={sharedProps}
            parentMatch={null}
            columnIsActive={!activePath}
            key={`${branch.match}${index}`}
          />
        ))}
      </div>
    </nav>
  )
}

function useMenuContent() {
  const data = useStaticQuery(graphql`
    {
      allContentfulSettings(
        filter: { contentful_id: { eq: "1NvlpsawwN2IEieW8uTZvy" } }
      ) {
        nodes {
          menuChapters {
            title
            slug
            chapterNumber
            galleries {
              title
              slug
            }
          }
          menuCollaborations {
            title
            slug
            chapterNumber
            galleries {
              title
              slug
            }
          }
          menuShop {
            ... on Node {
              ... on ContentfulProductDepartment {
                __typename
                title
                slug
                category {
                  slug
                }
                allLabel
                productTypes {
                  title
                  slug
                }
              }
              ... on ContentfulMenuLink {
                __typename
                label
                path: internalLink
              }
            }
          }
          menuAbout {
            title
            slug
          }
          # menuProjects {
          # 	title
          # 	slug
          # }
          menuContact {
            label
            href
            path: internalLink
            description {
              childMarkdownRemark {
                html
              }
            }
          }
          menuStockists {
            title
            slug
          }
          menuLegal {
            title
            slug
          }
        }
      }
    }
  `)
  const {
    menuChapters,
    menuCollaborations,
    menuShop,
    menuContact,
    menuStockists,
    menuLegal,
    menuAbout,
  } = data.allContentfulSettings.nodes[0]

  return [
    ...(menuShop?.map((entry) => {
      if (entry.__typename === "ContentfulProductDepartment") {
        const path = `/${entry.category.slug}/${entry.slug}` // eg. "/shop/mens"
        return {
          label: entry.title,
          path: path,
          match: path,
          links: [
            {
              label: entry.allLabel || "All",
              path: `${path}#thumbnails`,
              match: `${path}/all`,
            },
            ...(entry.productTypes?.map((type) => {
              return {
                label: type.title,
                path: `${path}/${type.slug}`,
                match: `${path}/${type.slug}`,
                willShowProduct: true,
              }
            }) || []),
          ],
        }
      } else if (entry.__typename === "ContentfulMenuLink") {
        return {
          label: entry.label,
          path: entry.path,
          // match: entry.path,
        }
      }
    }) || []),
    {
      label: "Chapters",
      // path: `/`,
      match: `/chapters`,
      links: [
        ...(menuChapters?.map((chapter) => {
          return {
            label: `${chapter.chapterNumber} ${chapter.title}`,
            path: `/${chapter.slug}`,
            match: `/chapters/${chapter.slug}`,
            links: chapter.galleries?.map((gallery) => {
              return {
                label: gallery.title,
                path: `/${chapter.slug}/${gallery.slug}`,
                match: `/chapters/${chapter.slug}/${gallery.slug}`,
              }
            }),
          }
        }) || []),
        {
          label: "All chapters",
          path: "/",
          match: "/",
        },
      ],
    },
    ...(menuCollaborations?.length > 0 ? [{
      label: "Collaborations",
      // path: `/`,
      match: `/collaborations`,
      links: [
        ...(menuCollaborations?.map((chapter) => {
          return {
            label: `${chapter.chapterNumber ? `${chapter.chapterNumber} ` : ''}${chapter.title}`,
            path: `/collaborations/${chapter.slug}`,
            match: `/collaborations/${chapter.slug}`,
            links: chapter.galleries?.map((gallery) => {
              return {
                label: gallery.title,
                path: `/collaborations/${chapter.slug}/${gallery.slug}`,
                match: `/collaborations/${chapter.slug}/${gallery.slug}`,
              }
            }),
          }
        }) || [])
      ],
    }] : []),
    // ...(menuProjects ? [{
    // 	label: 'Projects',
    // 	// path: '/projects',
    // 	match: '/projects',
    // 	links: menuProjects.map((project) => {
    // 		return {
    // 			label: project.title,
    // 			path: `/projects/${project.slug}`,
    // 			match: `/projects/${project.slug}`
    // 		}
    // 	})
    // }] : []),
    {
      label: "Info",
      match: `/info`,
      links: [
        ...(menuAbout?.map((link) => {
          return {
            label: link.title,
            path: `/info/${link.slug}`,
            match: `/info/${link.slug}`,
          }
        }) || []),
        {
          label: "Contact",
          path: `/info/contact`,
          match: `/info/contact`,
          links: menuContact,
        },
        {
          label: "Stockists",
          match: "/info/stockists",
          links: menuStockists.map((category) => {
            return {
              label: category.title,
              path: `/info/stockists/${category.slug}`,
              match: `/info/stockists/${category.slug}`,
            }
          }),
        },
        {
          label: "FAQs",
          match: "/info/faqs",
          links: menuLegal?.map((link) => {
            return {
              label: link.title,
              path: `/info/faqs/${link.slug}`,
              match: `/info/faqs/${link.slug}`,
            }
          }),
        },
      ],
    },
    {
      label: "Cart",
      match: `/cart`,
      willShowCart: true,
    },
  ]
}

export default Menu
